import React, { useState, useEffect, useRef } from "react";
import SaveButtonWithModal from "./SaveButtonWithModel";
import { uploadToCloudinary } from "../utils/CloudinaryUpload";
import { useCountry } from '../pages/CountryContext';
const LOCAL_STORAGE_KEY = "productServiceFormData";

const ProductService = ({
  formData,
  onProductImagesChange,
  onFormDataChange,
  selectedOption,
  onCertificatesUrlChange,
  onIdUrlChange,
  onOptionChange,
  onSave,
  onBoxValuesChange, // Add this prop
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCircles, setSelectedCircles] = useState({});
  const [images, setImages] = useState(Array(8).fill(null));
  const [certFiles, setCertFiles] = useState(Array(8).fill(null));
  const [showModal, setShowModal] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const handleEditClick = (index) => {
    setEditingIndex(index === editingIndex ? null : index);
  };
  const [error, setError] = useState("");
  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("Products / Services");
  const [boxPlaceholders, setBoxPlaceholders] = useState([
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
  ]);
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const [boxValues, setBoxValues] = useState(Array(8).fill({
    minOrder: 0,
    minOrderUnit: 'Kg',
    size: 'Regular',
    farmGatePrice: 0,
    farmGatePriceUnit: 'Kg',
    documentType: '' // Add this field
  }));
  const [unitOptions] = useState({
    minOrderUnit: ['Kg', 'Litres', 'Units', 'Bags', 'Boxes'],
    farmGatePriceUnit: ['Kg', 'Litres', 'Units', 'Bags', 'Boxes']
  });
  const handleUnitChange = (index, field, direction) => {
    const units = ['Kg', 'Litres', 'Units', 'Bags', 'Boxes'];
    setBoxValues(prev => {
      const newValues = [...prev];
      const currentUnit = newValues[index][field];
      const currentIndex = units.indexOf(currentUnit);
      
      let newIndex;
      if (direction === 'up') {
        newIndex = currentIndex < units.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : units.length - 1;
      }
      
      newValues[index] = {
        ...newValues[index],
        [field]: units[newIndex]
      };
      return newValues;
    });
  };
  
  const handleIncrement = (index, field) => {
    setBoxValues(prev => {
      const newValues = [...prev];
      newValues[index] = {
        ...newValues[index],
        [field]: newValues[index][field] + 1
      };
      return newValues;
    });
  };
  
  const handleDecrement = (index, field) => {
    setBoxValues(prev => {
      const newValues = [...prev];
      newValues[index] = {
        ...newValues[index],
        [field]: Math.max(0, newValues[index][field] - 1)
      };
      return newValues;
    });
  };
  // Add this at the top of ProductService.jsx
const calculateFinalPrice = (basePrice) => {
  const markup = basePrice * 0.05; // 5% markup
  return Math.round((basePrice + markup) * 100) / 100; // Round to 2 decimal places
};
  const handleSizeChange = (index, direction) => {
    const sizes = ['Regular', 'Medium', 'Large', 'X-Large'];
    setBoxValues(prev => {
      const newValues = [...prev];
      const currentSize = newValues[index].size;
      const currentIndex = sizes.indexOf(currentSize);
      
      let newIndex;
      if (direction === 'up') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
      } else {
        newIndex = currentIndex < sizes.length - 1 ? currentIndex + 1 : currentIndex;
      }
      
      newValues[index] = {
        ...newValues[index],
        size: sizes[newIndex]
      };
      return newValues;
    });
  };
  // Scroll functionality
  // In ProductService.jsx
  const handleBoxValueChange = (index, field, value) => {
    setBoxValues(prev => {
      const newValues = [...prev];
      newValues[index] = {
        ...newValues[index],
        [field]: value
      };
      return newValues;
    });
  };
  const handleNumberChange = (index, field, value) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    if (!isNaN(numValue)) {
      handleBoxValueChange(index, field, numValue);
    }
  };

  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      setImages(parsed.images || Array(8).fill(null));
      setBoxPlaceholders(parsed.boxPlaceholders || [
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
      ]);
      setCurrentBoxIndex(parsed.currentBoxIndex || 0);
      setSelectedDocumentType(parsed.selectedDocumentType || "Products / Services");
      
      // Update parent component with restored images
      if (parsed.images) {
        const filteredImages = parsed.images.filter(img => img !== null);
        onProductImagesChange(filteredImages);
      }
    }
  }, []);

  // Calculate thumb height based on content height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [documentDropdownOpen]);

  // Handle scroll events
  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  // Mouse down on thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  // Mouse move when dragging thumb
  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  // Mouse up when dragging thumb
  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  // Click on track to jump to position
  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  const documentTypes = [
    "National Identity", "Passport", "Drivers License", "Business registration",
    "Business License", "B-BBEE- Affidavit/Certificate", "Trade certificates",
    "Health Certificate", "GAP certificate", "Training Certificate",
    "Industry-Specific Licenses or Permits", "Membership of organization",
    "Awards", "Bank Account", "Business Insurance", "Business Plan & Financial Records",
    "Title deed", "Lease agreement", "Tax Clearance", "Others"
  ];

  const handleDeleteImage = (index) => {
    const newImages = [...images];
    newImages[index] = null;
    setImages(newImages);
    
    const newFiles = [...certFiles];
    newFiles[index] = null;
    setCertFiles(newFiles);
  
    // Reset the placeholder for this box only
    setBoxPlaceholders(prev => {
      const newPlaceholders = [...prev];
      newPlaceholders[index] = "XXXXXXX";
      return newPlaceholders;
    });
  
    // Reset the box values for this index
    setBoxValues(prev => {
      const newBoxValues = [...prev];
      newBoxValues[index] = {
        minOrder: 0,
        minOrderUnit: 'Kg',
        size: 'Regular',
        farmGatePrice: 0,
        farmGatePriceUnit: 'Kg',
        documentType: ''
      };
      return newBoxValues;
    });
  
    const filteredImages = newImages.filter(img => img !== null);
    if (onProductImagesChange) {
      onProductImagesChange(
        filteredImages.map(img => ({
          url: img.previewUrl,
          type: img.documentType,
          file: img.file
        }))
      );
    }
  };

  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const imageData = {
      previewUrl: URL.createObjectURL(file),
      url: URL.createObjectURL(file),
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      documentType: boxPlaceholders[index] || selectedDocumentType, // Use the box placeholder or selected type
      cloudinaryUrl: null
    };
  
    const newImages = [...images];
    newImages[index] = imageData;
    setImages(newImages);
    
    if (onProductImagesChange) {
      onProductImagesChange(
        newImages.filter(img => img !== null).map(img => ({
          url: img.previewUrl,
          type: img.documentType,
          file: img.file
        }))
      );
    }
  };

  const handleIdChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdFile(file);
      setIdImageUrl(URL.createObjectURL(file));
    }
  };

  const handleDocumentTypeSelect = (type) => {
    setSelectedDocumentType(type);
    setDocumentDropdownOpen(false);
    
    // Find the first empty box or use current index if all are filled
    let targetIndex = currentBoxIndex;
    const emptyIndex = boxPlaceholders.findIndex(p => p === "XXXXXXX");
    if (emptyIndex !== -1) {
      targetIndex = emptyIndex;
    }
  
    setBoxPlaceholders(prev => {
      const newPlaceholders = [...prev];
      newPlaceholders[targetIndex] = type;
      return newPlaceholders;
    });
  
    // Move to next index (cycling if needed)
    setCurrentBoxIndex((prevIndex) => (prevIndex + 1) % 8);
  };
const [productImages, setProductImages] = useState([]);
  const [setDocumentTypes] = useState(Array(8).fill(""));
  const handleDocumentTypeChange = (index, type) => {
    const newDocumentTypes = [...documentTypes];
    newDocumentTypes[index] = type;
    setDocumentTypes(newDocumentTypes);
    
    // Update the product images with document type
    const updatedImages = [...productImages];
    if (updatedImages[index]) {
      updatedImages[index] = {
        ...updatedImages[index],
        documentType: type
      };
    }
    setProductImages(updatedImages);
    onProductImagesChange(updatedImages);
  };

  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img && typeof img === 'string' && img.startsWith('blob:')) {
          URL.revokeObjectURL(img);
        }
      });
      if (idImageUrl) URL.revokeObjectURL(idImageUrl);
    };
  }, [images, idImageUrl]);
  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      setImages(parsed.images || Array(8).fill(null));
      setBoxPlaceholders(parsed.boxPlaceholders || Array(8).fill("XXXXXXX"));
      setCurrentBoxIndex(parsed.currentBoxIndex || 0);
      setSelectedDocumentType(parsed.selectedDocumentType || "Products / Services");
      setBoxValues(parsed.boxValues || Array(8).fill({
        minOrder: 0,
        minOrderUnit: 'Kg',
        size: 'Regular',
        farmGatePrice: 0,
        farmGatePriceUnit: 'Kg'
      }));
      
      if (parsed.images) {
        const filteredImages = parsed.images.filter(img => img !== null);
        onProductImagesChange(filteredImages);
      }
    }
  }, []);
  const handleSave = async (data) => {
    setSaving(true);
    setError("");
    
    try {
      // Upload all images to Cloudinary first
      const uploadPromises = images
        .filter(img => img !== null && img.file)
        .map(async (img, index) => {
          try {
            const uploadResult = await uploadToCloudinary(img.file, {
              folder: 'products',
              public_id: `${formData.registrationNumber || 'temp'}_${index}_${Date.now()}`
            });
            
            return {
              ...img,
              cloudinaryUrl: uploadResult.secure_url,
              url: uploadResult.secure_url, // Use Cloudinary URL as primary URL
              documentType: boxPlaceholders[index] || img.documentType || 'Product',
              // Remove the file object to avoid serialization issues
              file: undefined
            };
          } catch (uploadError) {
            console.error(`Error uploading image ${index}:`, uploadError);
            return null;
          }
        });
  
      // Wait for all uploads to complete
      const uploadedImages = (await Promise.all(uploadPromises)).filter(img => img !== null);
      
      // Update local state with Cloudinary URLs
      const updatedImages = images.map((img, index) => {
        if (!img) return null;
        // Find matching uploaded image
        const uploaded = uploadedImages.find(u => 
          u?.name === img.name || 
          u?.documentType === boxPlaceholders[index]
        );
        return uploaded || {
          ...img,
          // If no upload but has cloudinaryUrl, keep it
          url: img.cloudinaryUrl || img.url,
          // Remove file object
          file: undefined
        };
      });
  
      setImages(updatedImages);
  
      // Prepare data for saving
      const dataToSave = {
        images: updatedImages,
        boxPlaceholders,
        currentBoxIndex,
        selectedDocumentType,
        boxValues
      };
  
      // Save to localStorage with Cloudinary URLs
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
      
      // Call parent's onSave with permanent URLs
      onSave({
        ...formData,
        productImages: updatedImages.filter(img => img !== null),
        typeOfOrganization: selectedOption,
        boxValues
      });
  
      setShowModal(true);
    } catch (error) {
      console.error("Upload error:", error);
      setError("Failed to upload product images. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        
        // Process images - prioritize cloudinaryUrl over previewUrl
        const processedImages = (parsed.images || Array(8).fill(null)).map(img => {
          if (!img) return null;
          return {
            ...img,
            url: img.cloudinaryUrl || img.url, // Use Cloudinary URL if available
            previewUrl: img.cloudinaryUrl || img.previewUrl // Fallback to previewUrl
          };
        });
  
        setImages(processedImages);
        setBoxPlaceholders(parsed.boxPlaceholders || Array(8).fill("XXXXXXX"));
        setCurrentBoxIndex(parsed.currentBoxIndex || 0);
        setSelectedDocumentType(parsed.selectedDocumentType || "Products / Services");
        setBoxValues(parsed.boxValues || Array(8).fill({
          minOrder: 0,
          minOrderUnit: 'Kg',
          size: 'Regular',
          farmGatePrice: 0,
          farmGatePriceUnit: 'Kg'
        }));
        
        // Update parent component with restored images
        const filteredImages = processedImages.filter(img => img !== null);
        if (filteredImages.length > 0 && onProductImagesChange) {
          onProductImagesChange(filteredImages.map(img => ({
            url: img.url,
            type: img.documentType,
            // Don't pass the file object as it can't be serialized
            file: undefined
          })));
        }
      } catch (e) {
        console.error("Error parsing saved data:", e);
        // Clear corrupted data
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  }, []);
    const { selectedCountry } = useCountry() || {};
  const currencySymbol = selectedCountry?.currency || 'R'; // Default to 'R' if no currency found
const handleProductImageChange = (index, file) => {
  const newProductImages = [...productImages];
  newProductImages[index] = {
    file,
    previewUrl: URL.createObjectURL(file),
    documentType: boxValues[index]?.documentType || boxPlaceholders[index]
  };
  setProductImages(newProductImages);
};
const UnselectedIcon = () => (
  <svg
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
  >
    <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" stroke="none" />
  </svg>
);

const SelectedIcon = () => (
  <svg
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
  >
    <circle cx="8.5" cy="8.5" r="8" fill="#2E453D" stroke="#FFBD26" />
    <path
      d="M5 8.5L7.5 11L12 6"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
  return (
    <div className="md:px-0 md:mt-6">
      <div className="">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <div 
              className="relative cursor-pointer"
              onClick={() => setDocumentDropdownOpen(!documentDropdownOpen)}
            >
              <div className="flex items-center md:gap-32 gap-1">
                <span className="text-[8px] md:text-[36px] font-bold">
                  {selectedDocumentType}
                </span>
                <svg
                  width={window.innerWidth > 768 ? "32" : "12"}
                  height={window.innerWidth > 768 ? "32" : "12"}
                  viewBox="0 0 24 24"
                  fill="none"
                  className={`text-black transition-transform ${
                    documentDropdownOpen ? "rotate-180" : ""
                  }`}
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              
              {documentDropdownOpen && (
  <div className="absolute top-full left-0 mt-2 md:mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-lg py-2 w-[300px] md:w-[300px] max-h-[400px] flex">
    <div className="flex-1 flex flex-col">
      <div className="px-3 md:px-2 md:pb-2 flex justify-between">
        <h1 className="text-[6px] md:text-[20px] font-bold text-black">
          Products / Services
        </h1>
        <img
          src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750034880/Export_1_vurzim.png"
          alt="Close dropdown"
          className="md:w-[43px] w-2 h-2 md:h-[42px] cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setDocumentDropdownOpen(false);
          }}
        />
      </div>

      <div className="w-full h-5 flex justify-end">
        <img
          src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png"
          alt="scroll-up"
          className="md:w-4 md:h-4 h-2 w-2 cursor-pointer"
          onClick={() => {
            contentRef.current.scrollBy({
              top: -50,
              behavior: "smooth",
            });
          }}
        />
      </div>

      <div className="custom-scroll-area">
        <div
          className="custom-scroll-content"
          ref={contentRef}
          onScroll={handleScroll}
        >
          <h2 className="px-4 md:pt-3 pb-1 text-[6px] md:text-[20px] font-bold underline text-gray-700">
            CROPS
          </h2>
          {[
            "African nightshade",
            "Alovera",
            "Avocados",
            "Apple",
            "Bananas",
            "Baobab",
            "Beans",
            "Beetroot",
            "Black nightshade",
            "Cabbage",
            "Capsicum (Bell Pepper)",
            "Cassava",
            "Castor Beans",
            "Chili pepper",
            "Cocoa",
            "Coffee",
            "Cotton",
            "Cowpeas",
            "Dragon Fruit",
            "Eggplant",
            "Garlic",
            "Ginger",
            "Groundnuts (peanuts)",
            "Guavas",
            "Hemp (Cannabis)",
            "Jatropha",
            "Jute mallow",
            "Kale",
            "Lentils",
            "Lemon",
            "Lime",
            "Maize (corn)",
            "Millet",
            "Mango",
            "Moringa",
            "Okra",
            "Onion",
            "Orange",
            "Palm oil",
            "Pineapples",
            "Plantain",
            "Potatoes (Irish)",
            "Potatoes (Yelow Flesh)",
            "Pumpkin",
            "Pinapples",
            "Rice",
            "Sorghum",
            "Soybeans",
            "Spider plant",
            "Spinach",
            "Spring Onions",
            "Strawberry",
            "Sugarcane",
            "Sunflower",
            "Sweet potatoes",
            "Tangarine",
            "Tea",
            "Tomatoes",
            "Water melon",
            "Vanilla",
            "Yam",
          ].map((type, index) => (
            <div
              key={`crop-${index}`}
              className="px-4 py-1 md:py-2 flex justify-between items-center hover:bg-gray-100 cursor-pointer text-[6px] md:text-[18px]"
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentTypeSelect(type);
              }}
            >
              {type}
              {selectedDocumentType === type ? <SelectedIcon /> : <UnselectedIcon />}
            </div>
          ))}

          <h2 className="px-4 pt-3 pb-1 text-[6px] md:text-[20px] font-bold underline text-gray-700">
            LIVESTOCK
          </h2>
          {[
            "Bees",
            "Cattle - Dairy",
            "Cattle- Zebu",
            "Cattle- Ankole",
            "Cattle- Nguni",
            "Cattle- Afrikaner",
            "Cattle- N'Dama",
            "Camels",
            "Chicken",
            "Donkeys",
            "Ducks",
            "Gunea Fowls",
            "Goats-Red Sokoto",
            "Goats-Boer Giant",
            "Goats-Sahelian",
            "Goats-East African",
            "Horses",
            "Pigs",
            "Rabbit",
            "Sheep- West African Dwarf",
            "Sheep- Dorper",
            "Sheep- Fat Tailed",
            "Sheep- Blackhead Persian",
            "Turkey",
          ].map((type, index) => (
            <div
              key={`livestock-${index}`}
              className="px-4 py-1 md:py-2 flex justify-between items-center hover:bg-gray-100 cursor-pointer text-[6px] md:text-[18px]"
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentTypeSelect(type);
              }}
            >
              {type}
              {selectedDocumentType === type ? <SelectedIcon /> : <UnselectedIcon />}
            </div>
          ))}

          <h2 className="px-4 pt-3 pb-1 text-[6px] md:text-[20px] font-bold underline text-gray-700">
            FISH
          </h2>
          {[
            "Catfish",
            "Common Carp",
            "Trout",
            "Tilapia",
          ].map((type, index) => (
            <div
              key={`fish-${index}`}
              className="px-4 py-1 md:py-2 flex justify-between items-center hover:bg-gray-100 cursor-pointer text-[6px] md:text-[18px]"
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentTypeSelect(type);
              }}
            >
              {type}
              {selectedDocumentType === type ? <SelectedIcon /> : <UnselectedIcon />}
            </div>
          ))}

          <h2 className="px-4 pt-3 pb-1 text-[6px] md:text-[20px] font-bold underline text-gray-700">
            FORESTRY
          </h2>
          {["Forest"].map((type, index) => (
            <div
              key={`forest-${index}`}
              className="px-4 py-1 md:py-2 flex justify-between items-center hover:bg-gray-100 cursor-pointer text-[6px] md:text-[18px]"
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentTypeSelect(type);
              }}
            >
              {type}
              {selectedDocumentType === type ? <SelectedIcon /> : <UnselectedIcon />}
            </div>
          ))}
        </div>
        <div
          className="custom-scroll-track"
          onClick={handleTrackClick}
        >
          <div
            ref={thumbRef}
            className="custom-scroll-thumb"
            style={{
              height: `${thumbHeight}px`,
              top: `${scrollTop}px`,
              cursor: isDragging ? "grabbing" : "grab",
            }}
            onMouseDown={handleThumbMouseDown}
          ></div>
        </div>
      </div>

      <div className="w-full h-5 flex justify-end">
        <img
          src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png"
          alt="scroll-down"
          className="md:w-4 md:h-4 h-2 w-2 cursor-pointer"
          onClick={() => {
            contentRef.current.scrollBy({
              top: 50,
              behavior: "smooth",
            });
          }}
        />
      </div>
    </div>
  </div>
)}

            </div>
          </div>
          <div className="flex justify-between items-center">
            <p className="font-normal italic text-[6px] md:text-[24px]">
              Please insert pictures / images of products or services 
            </p>
          </div>
        </div>
        <div className="grid grid-cols-4 md:gap-2 gap-1 mb-3 md:mb-6">
  {images.slice(0, 8).map((imgSrc, index) => (
    <div key={index} className="flex flex-col md:mt-6">
      <p className="text-[6px] md:text-[24px] font-light italic md:mb-3 text-left min-h-[12px] md:w-[260px] leading-tight">
        {boxPlaceholders[index]}
      </p>
      <div className="relative bg-[#E7EBEB] rounded-[5px] h-[160px] md:h-[450px] w-full overflow-hidden">
        {imgSrc && (
          <img
            src={imgSrc.previewUrl || imgSrc}
            alt="Certificate"
            className="object-cover w-full h-full text-[6px]"
          />
        )}
        <div className="absolute bottom-0 left-0 right-0 md:pr-10 p-2 rounded-b-[5px]">
          {/* Minimum Order Section */}
          <div>
            <label className="text-black text-[4px] md:text-[14px] font-normal block md:mb-1">Minimum Order</label>
            <div className="flex items-center ">
              <div className="flex-1 flex bg-[#CFD7D7] rounded-l md:h-full h-[14px] overflow-hidden">
                <div className="md:w-3/4 md:px-0 px-1 flex items-center justify-center bg-[#CFD7D7] text-black text-[4px] md:text-[14px] md:py-1">
                  {boxValues[index].minOrderUnit}
                </div>
                <div className="md:w-1/4 w-full flex flex-col justify-center items-cente ">
                  <button 
                    className="text-black hover:opacity-80"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnitChange(index, 'minOrderUnit', 'up');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-1 md:w-4 h-1 md:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button 
                    className="text-black hover:opacity-80"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnitChange(index, 'minOrderUnit', 'down');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-1 md:w-4 h-1 md:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
              <input
                type="number"
                value={boxValues[index].minOrder}
                onChange={(e) => handleNumberChange(index, 'minOrder', e.target.value)}
                className="flex-1 md:h-full h-[0px] bg-[#9FAFAF] border-[#9FAFAF] text-black text-[4px] md:text-[14px] py-1.5 px-1 rounded-r text-center w-full"
                min="0"
              />
            </div>
          </div>

          {/* Size Section */}
          <div className="md:mb-2 mb-[6px] w-3/6">
            <label className="text-black text-[4px] md:text-[14px] font-normal block md:mb-1">Size</label>
            <div className="flex-1 justify-between md:mb-0 mb-1 md:px-3 px-1 flex md:h-full h-[12px] bg-[#CFD7D7] rounded-l overflow-hidden">
              <div className="flex items-center justify-center text-black text-[4px] md:text-[14px] md:py-1">
                {boxValues[index].size}
              </div>
              <div className="flex flex-col justify-center items-center">
                <button 
                  className="text-black hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSizeChange(index, 'up');
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-[4px] md:w-4 h-[4px] md:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button 
                  className="text-black hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSizeChange(index, 'down');
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-1 md:w-4 h-1 md:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Size Options */}
          <div className="grid grid-cols-1 gap-[2px] md:gap-1">
            {['Regular', 'Medium', 'Large', 'X-Large'].map((size, idx) => (
              <div
                key={idx}
                className={`${boxValues[index].size === size ? 'bg-[#9FAFAF]' : 'bg-[#CFD7D7]'} text-black text-[4px] flex justify-center items-center w-2/4 md:text-[12px] md:h-full h-[10px] md:py-1 text-center rounded-l`}
              >
                {size}
              </div>
            ))}
          </div>

          {/* Farm Gate Price Section */}
          <div>
            <label className="text-black text-[4px] md:text-[14px] font-normal block md:mb-1">Farm Gate Price</label>
            <div className="flex items-center ">
              <div className="bg-[#2E453D] flex rounded-l overflow-hidden md:w-[200px] w-[90px] md:h-full h-[16px]">
                <div className="flex-1 px-1 flex items-center justify-center bg-[#2E453D] text-white text-[4px] md:text-[14px] md:py-1.5">
                  {boxValues[index].farmGatePriceUnit}
                </div>
                <div className="flex flex-col justify-center items-center pr-2">
                  <button 
                    className="text-white hover:opacity-80 w-full flex justify-center items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnitChange(index, 'farmGatePriceUnit', 'up');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-1 md:w-[13px] h-1 md:h-[13px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button 
                    className="text-white hover:opacity-80 w-full flex justify-center items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnitChange(index, 'farmGatePriceUnit', 'down');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-1 md:w-[13px] h-1 md:h-[13px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex items-center md:w-[200px] justify-center bg-[#152C24] rounded-r">
                <span className="text-white font-bold text-[4px] md:text-[14px] px-1">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={boxValues[index].farmGatePrice}
                  onChange={(e) => handleNumberChange(index, 'farmGatePrice', e.target.value)}
                  className="md:h-full h-[0px] w-[40px] bg-transparent border-none text-white text-[4px] font-bold md:text-[14px] py-2 px-1 focus:outline-none"
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Icons in top right corner */}
        <div className="absolute top-1 right-1 md:top-3 md:right-3 flex gap-1 md:gap-2">
          {/* Edit Icon */}
          
          {/* Add Icon */}
          <label className="cursor-pointer">
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745668623/Add_square_duotone_gz7vxe.png"
              alt="Add Icon"
              className="w-3 md:w-[24px] h-3 md:h-[24px]"
            />
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleImageChange(e, index)}
              className="hidden"
            />
          </label>
        </div>
        <div className="absolute top-8 right-2 md:top-24 md:right-6">
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1752450366/Edit_fill_f7qrjq.png"
              alt="Edit Icon"
              className="w-2 md:w-[24px] h-2 md:h-[24px]"
              onClick={() => handleEditClick(index)} // Add your edit handler function
            />
          </div>
          
        {/* Trash Icon */}
        <div className="absolute md:bottom-2 md:right-2 bottom-0 right-1">
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1746296659/Trash_ya7kiu.png"
            alt="Trash Icon"
            className="w-2 md:w-[24px] h-2 md:h-[24px] cursor-pointer"
            onClick={() => handleDeleteImage(index)}
          />
        </div>
      </div>
    </div>
  ))}
</div>
      </div>
      {error && (
        <div className="mt-2 text-red-600 text-xs text-center">{error}</div>
      )}
      <SaveButtonWithModal onClick={handleSave} loading={saving} />

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 300px;
          display: flex;
          overflow: hidden;
          width: 100%;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 4px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 8px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
            .custom-scroll-area {
          position: relative;
          max-height: 150px;
          display: flex;
          overflow: hidden;
          width: 100%;
        }
        }
      `}</style>
    </div>
  );
};

export default ProductService;
